import { MatchPlayerStatsFieldsFragment } from '../../graphql/__generated__/graphqlApi';
import { TCustomFunction } from '../context/LangContext';
import { DataStats } from '../../containers/partials/RaceResultContainer';
import isEmpty from 'lodash/isEmpty';
import { LOCKED } from '../../constants/Security';
import { InplayMarketGroupFieldsType, RaceResultType } from '../../types/match';

const getGameResults = (
  raceResults: RaceResultType,
  resultType: '4wDao' | 'sumo' | 'yadokari',
  t: TCustomFunction,
) => {
  const num = resultType === 'yadokari' ? 2 : 3;
  const colorCodeIndex = raceResults?.headers.indexOf('colorCode');

  const raceResultHeaders = raceResults?.headers.filter(
    (_, headerIndex) => headerIndex !== colorCodeIndex,
  );

  const [headers1, headers2] = splitArrays(raceResultHeaders, num);

  const results =
    raceResults.rows.map((itemResult, index) => {
      const itemResultValues = itemResult.values.filter(
        (_, valueIndex) => valueIndex !== colorCodeIndex,
      );

      const colorCode = itemResult.values.find(
        (_, valueIndex) => valueIndex === colorCodeIndex,
      );

      const [result1, result2] = splitArrays(itemResultValues, num);
      return {
        result1: result1.map((itemResult1, indexRow) => ({
          value: itemResult1,
          id: `${itemResult1}${indexRow}`,
        })),
        result2: result2.map((itemResult2, indexRow) => ({
          value: itemResult2,
          id: `${itemResult2}${indexRow}`,
        })),
        id: `key_${resultType}_race_results_${index}`,
        colorCode,
      };
    }) ?? [];

  return {
    results,
    headers1: headers1.map(itemHeader1 => t(itemHeader1)),
    headers2: headers2.map(itemHeader2 => t(itemHeader2)),
    title: t('raceResult'),
  };
};

export const getRaceResults = (
  data: InplayMarketGroupFieldsType | null,
  t: TCustomFunction,
): DataStats | null => {
  if (!data) return null;

  if (!isEmpty(data.fourwDao)) {
    return getGameResults(data.fourwDao, '4wDao', t);
  }
  if (!isEmpty(data.sumoDao)) {
    return getGameResults(data.sumoDao, 'sumo', t);
  }
  if (!isEmpty(data.yadokariDao)) {
    return getGameResults(data.yadokariDao, 'yadokari', t);
  }
  return null;
};

const splitArrays = (data: string[], num?: number) => {
  const firstNumber = num ?? 2;
  if (data.length <= firstNumber) {
    return [data, []];
  }
  const firstThree = data.slice(0, firstNumber);
  const rest = data.slice(firstNumber);
  return [firstThree, rest];
};

const mapResult = (data: string[]) => {
  return splitArrays(data).map(results =>
    results.map((value, indexRow) => ({
      isLocked: value.includes(LOCKED),
      value,
      id: `${value}${indexRow}`,
    })),
  );
};

export const getPlayerStats = (
  t: TCustomFunction,
  matchPlayerStatsData: MatchPlayerStatsFieldsFragment | null,
): DataStats[] => {
  if (!matchPlayerStatsData?.results.length) {
    return [];
  }
  return matchPlayerStatsData.results.map(item => {
    const colorCodeIndex = item.headers.indexOf('colorCode');

    const headerData = item.headers.filter(
      (_, index) => index !== colorCodeIndex,
    );

    const [headers1, headers2] = splitArrays(headerData).map(headers =>
      headers.map(header => t(header)),
    );

    const resultStats = item.rows.map((itemRow, index) => {
      const rowData = itemRow.values.filter(
        (_, valueIndex) => valueIndex !== colorCodeIndex,
      );

      const colorCode = itemRow.values.find(
        (_, valueIndex) => valueIndex === colorCodeIndex,
      );

      const [result1, result2] = mapResult(rowData);

      return {
        result1,
        result2,
        id: `key_stats_${index}`,
        colorCode,
      };
    });
    return {
      headers1,
      headers2,
      results: resultStats,
      title: item.groupTag?.name,
    };
  });
};
